import { cn } from '../cn.js';
import { Avatar } from '../components/avatar.js';
import { buttonVariants } from '../components/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuGroupLabel,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/menu.js';
import { IconChevronDown, IconSettings, IconSignOut } from '../icons.js';

/** One preference chosen from a few: the theme, the language, how tightly to pack the screen. */
export interface AccountMenuPreference {
  readonly id: string;
  /** What the group is called, shown above its choices. */
  readonly label: string;
  readonly value: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly onChange: (value: string) => void;
}

export interface AccountMenuProps {
  /** Who is signed in. Without one, the trigger is a preferences button, for the sign-in pages. */
  readonly name?: string | undefined;
  /** A second line under the name inside the menu: an address, or a role. */
  readonly detail?: string | undefined;
  /** The trigger's accessible name, which says what is behind it rather than who. */
  readonly label: string;
  readonly preferences: readonly AccountMenuPreference[];
  readonly signOutLabel?: string | undefined;
  readonly onSignOut?: (() => void) | undefined;
  readonly className?: string | undefined;
}

/**
 * Who is signed in, their preferences, and the way out — behind one control.
 *
 * Before this, the workspace header held a full-width select for the language, another for the
 * theme and a third for density, and they were the largest controls on every screen: preferences
 * somebody sets once, given more weight than the navigation they sat beside (M5.5-01). M4.5-02's
 * research had already placed the theme "in the profile menu or the command palette"; this is the
 * profile menu.
 *
 * Each preference is a radio group, so the menu shows the current choice every time it opens —
 * which is what makes a menu an acceptable home for state. Sign-out is last and set apart, because
 * it is the one item here that ends something.
 */
export function AccountMenu({
  name,
  detail,
  label,
  preferences,
  signOutLabel,
  onSignOut,
  className,
}: AccountMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={label}
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'sm' }),
          'max-w-(--width-menu) gap-(--space-xs) data-[popup-open]:bg-(--color-surface-sunken)',
          className,
        )}
      >
        {name === undefined ? (
          <IconSettings aria-hidden="true" className="size-(--size-icon-sm)" />
        ) : (
          <>
            <Avatar name={name} size="sm" className="size-(--size-icon-lg)" />
            {/* The name is the trigger's text on a wide screen and its avatar alone on a narrow
                one; the accessible name is the label either way. */}
            <span className="hidden min-w-0 truncate sm:inline">{name}</span>
          </>
        )}
        <IconChevronDown
          aria-hidden="true"
          className="size-(--size-icon-sm) shrink-0 text-(--color-text-muted)"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-(--width-menu)">
        {name === undefined ? null : (
          <>
            <div className="flex flex-col px-(--space-sm) py-(--space-xs)">
              <span className="truncate text-(length:--font-size-body) font-medium text-(--color-text-primary)">
                {name}
              </span>
              {detail === undefined ? null : (
                <span className="truncate text-(length:--font-size-caption) text-(--color-text-muted)">
                  {detail}
                </span>
              )}
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        {preferences.map((preference, index) => (
          <DropdownMenuGroup key={preference.id}>
            {index === 0 ? null : <DropdownMenuSeparator />}
            <DropdownMenuGroupLabel>{preference.label}</DropdownMenuGroupLabel>
            <DropdownMenuRadioGroup
              value={preference.value}
              onValueChange={(value) => preference.onChange(String(value))}
            >
              {preference.options.map((option) => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuGroup>
        ))}
        {onSignOut === undefined || signOutLabel === undefined ? null : (
          <>
            <DropdownMenuSeparator />
            {/* Drawn as destructive: it ends the session, and whatever is on screen with it. */}
            <DropdownMenuItem destructive onClick={onSignOut}>
              <IconSignOut aria-hidden="true" className="size-(--size-icon-sm)" />
              {signOutLabel}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
